const { buildContext } = require('./context');
const { createReport } = require('./report');
const { fetchDiagnostics } = require('./apiClient');
const { renderDebugOverlay } = require('./overlay');
const { STATUS, CHECK_ID } = require('./constants');
const implementation = require('./checks/implementation');
const ottuPg = require('./checks/ottuPg');
const jazz = require('./checks/jazz');
const cors = require('./checks/cors');
const csp = require('./checks/csp');

const CHECK_TIMEOUT_MS = 4000;

// Race a check against a cancelable timer so a hung check (e.g. the CSP
// violation-collection wait with no network response) can never stall the whole
// report. The timer is cleared as soon as the real promise settles, so tests
// leave no orphan.
function withTimeout(promise, ms, onTimeout) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(onTimeout()), ms);
  });
  const settled = Promise.resolve(promise).then(
    (v) => { clearTimeout(timer); return v; },
    (e) => { clearTimeout(timer); throw e; },
  );
  return Promise.race([settled, timeout]);
}

async function safe(id, fn, timeoutMs) {
  try {
    return await withTimeout(
      Promise.resolve().then(fn),
      timeoutMs,
      () => ({ id, status: STATUS.INFO, detail: 'check timed out', evidence: { observedOutcome: 'unavailable' } }),
    );
  } catch (e) {
    return { id, status: STATUS.INFO, detail: 'check failed to run', evidence: { error: e && e.message } };
  }
}

async function runDiagnostics(opts = {}) {
  const { scriptElement = null, render = true, checkTimeoutMs = CHECK_TIMEOUT_MS } = opts;

  let ctx;
  try {
    ctx = buildContext(scriptElement);
  } catch (e) {
    // Context assembly must never break the page — degrade to a minimal ctx.
    ctx = { sdkVersion: '', merchant_id: '', shopOrigin: '', ottuInstanceDomain: '', paymentMethods: [] };
  }

  // Bound the diagnostics fetch too — sdkFetch has no AbortController, so a
  // stalled network response could otherwise hang an awaited runDiagnostics().
  let apiConfig = null;
  try {
    apiConfig = await withTimeout(
      Promise.resolve().then(() => fetchDiagnostics(ctx)),
      checkTimeoutMs,
      () => null,
    );
  } catch (e) {
    apiConfig = null;
  }

  const checks = await Promise.all([
    safe(CHECK_ID.IMPLEMENTATION, () => implementation.runImplementationCheck(ctx), checkTimeoutMs),
    safe(CHECK_ID.OTTU_PG, () => ottuPg.runOttuPgCheck(ctx, apiConfig), checkTimeoutMs),
    safe(CHECK_ID.JAZZ, () => jazz.runJazzCheck(ctx, apiConfig), checkTimeoutMs),
    safe(CHECK_ID.CORS, () => cors.runCorsCheck(ctx, apiConfig), checkTimeoutMs),
    safe(CHECK_ID.CSP, () => csp.runCspCheck(ctx, apiConfig), checkTimeoutMs),
  ]);

  let report;
  try {
    report = createReport(ctx, checks);
  } catch (e) {
    report = {
      schemaVersion: 1,
      generatedAt: new Date().toISOString(),
      sdkVersion: ctx.sdkVersion || '',
      merchant_id: ctx.merchant_id || '',
      shopOrigin: ctx.shopOrigin || '',
      ottuInstanceDomain: ctx.ottuInstanceDomain || '',
      checks,
    };
  }

  if (render) {
    try {
      renderDebugOverlay(report);
    } catch (e) {
      // overlay must never break the page
    }
  }
  return report;
}

// armCspCollection is re-exported so init (checkout.js) can arm the CSP listener
// before render through a single diagnostics import.
module.exports = { runDiagnostics, armCspCollection: csp.armCspCollection };
